import React, { useState, useEffect, useCallback } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, X, Shield, Check, Minus, Edit2, Trash2, ChevronDown, ChevronUp, Search } from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────
const ROLE_GRADIENT = {
  all:            { from: '#64748b', to: '#475569' },
  admin:          { from: '#ef4444', to: '#dc2626' },
  office_user:    { from: '#3b82f6', to: '#2563eb' },
  coordinator:    { from: '#a855f7', to: '#9333ea' },
  hr:             { from: '#22c55e', to: '#16a34a' },
  office_support: { from: '#f59e0b', to: '#d97706' },
  carer:          { from: '#06b6d4', to: '#0891b2' },
};

const ROLE_BADGE = {
  admin:          'bg-red-50 text-red-600 border border-red-200',
  office_user:    'bg-blue-50 text-blue-600 border border-blue-200',
  coordinator:    'bg-purple-50 text-purple-600 border border-purple-200',
  hr:             'bg-green-50 text-green-600 border border-green-200',
  office_support: 'bg-amber-50 text-amber-600 border border-amber-200',
  carer:          'bg-cyan-50 text-cyan-600 border border-cyan-200',
};

const MODULE_LABELS = {
  dashboard:'Dashboard', scheduler:'Scheduler', clients:'Clients',
  staff:'Staff', invoices:'Invoices', timesheets:'Timesheets',
  reports:'Reports', forms:'Forms', incidents:'Incidents',
  medications:'Medications', messages:'Messages', leave:'Leave',
  tasks:'Tasks', settings:'Settings', aged_care:'Aged Care',
};

const PERM_LEVELS = ['view','create','edit','delete'];
const PERM_COLORS = { view:'bg-sky-500', create:'bg-emerald-500', edit:'bg-amber-500', delete:'bg-red-500' };
const COLORS = ['#6366f1','#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316','#84cc16'];

// Pay and payroll permissions (Pay Rates v1.2 section 10). The list comes from the server
// (GET /roles/permissions payPermissions, backend/lib/payAccess.js). Each key is module.action and is
// stored in the role's matrix like any other tick; Roster: Edit is the Scheduler row's Edit.
function PayPermissionList({ items, permissions, onToggle }) {
  if (!items || !items.length) return null;
  return (
    <div className="border-t border-slate-200 px-4 py-3 bg-white">
      <p className="text-xs font-semibold text-slate-600 mb-0.5">Pay and payroll</p>
      <p className="text-[11px] text-slate-400 mb-2.5">Someone on this role gets only the ticks here, whatever their base role. Without a custom role, admins have all of these and office users have Pay Rates: View, Roster: Edit, both Timesheets permissions, Payroll: Send to Xero and Integrations: Xero.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map(p => {
          const [mod, action] = p.key.split('.');
          const on = Array.isArray(permissions[mod]) && permissions[mod].includes(action);
          return onToggle ? (
            <label key={p.key} className="flex items-start gap-2.5 rounded-lg border border-slate-100 px-3 py-2 hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={on} onChange={() => onToggle(mod, action)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-primary-600 cursor-pointer focus:ring-primary-500"/>
              <span className="min-w-0">
                <span className="block text-xs font-semibold text-slate-700">{p.label}</span>
                <span className="block text-[11px] text-slate-500 leading-snug">{p.help}</span>
              </span>
            </label>
          ) : (
            <div key={p.key} className="flex items-start gap-2.5 rounded-lg border border-slate-100 px-3 py-2">
              {on
                ? <div className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm"><Check size={11} className="text-white stroke-[3]"/></div>
                : <div className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-slate-100 flex items-center justify-center"><Minus size={10} className="text-slate-300"/></div>}
              <span className="min-w-0">
                <span className="block text-xs font-semibold text-slate-700">{p.label}</span>
                <span className="block text-[11px] text-slate-500 leading-snug">{p.help}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function roleDisplay(key) {
  return { admin:'Admin', office_user:'Office User', coordinator:'Coordinator', hr:'HR', office_support:'Office Support', carer:'Carer' }[key] || key;
}

// ── Role card ─────────────────────────────────────────────────────────────────
function RoleCard({ role, selected, onClick, isCustom, onEdit, onDelete }) {
  const grad = role.color
    ? { background: role.color }
    : { background: `linear-gradient(135deg, ${(ROLE_GRADIENT[role.key]||ROLE_GRADIENT.all).from}, ${(ROLE_GRADIENT[role.key]||ROLE_GRADIENT.all).to})` };

  return (
    <div onClick={onClick}
      className={`relative cursor-pointer rounded-2xl p-5 transition-all duration-200 group
        ${selected
          ? 'ring-2 ring-primary-500 shadow-lg shadow-primary-100/50 bg-white'
          : 'bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md'}`}>

      {/* Icon */}
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-md" style={grad}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      </div>

      <div className="text-3xl font-bold text-slate-800 leading-none mb-1">{role.count ?? 0}</div>
      <div className="text-sm text-slate-500 font-medium truncate">{role.name}</div>

      {selected && (
        <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-primary-500"/>
      )}

      {isCustom && role.id && (
        <div className="absolute top-2.5 right-2.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={e => { e.stopPropagation(); onEdit(role); }}
            className="w-6 h-6 rounded-lg bg-white shadow border border-slate-100 flex items-center justify-center text-slate-400 hover:text-blue-600">
            <Edit2 size={11}/>
          </button>
          <button onClick={e => { e.stopPropagation(); onDelete(role); }}
            className="w-6 h-6 rounded-lg bg-white shadow border border-slate-100 flex items-center justify-center text-slate-400 hover:text-red-500">
            <Trash2 size={11}/>
          </button>
        </div>
      )}
    </div>
  );
}

// ── New/Edit role modal ───────────────────────────────────────────────────────
function RoleModal({ role, basePermissions, payPermissions, onClose, onSaved }) {
  const [name, setName]               = useState(role?.name        || '');
  const [description, setDescription] = useState(role?.description || '');
  const [color, setColor]             = useState(role?.color        || '#6366f1');
  const [basedOn, setBasedOn]         = useState('');
  const [permissions, setPermissions] = useState(role?.permissions  || {});
  const [showPerms, setShowPerms]     = useState(false);
  const [saving, setSaving]           = useState(false);

  function togglePerm(mod, level) {
    setPermissions(p => {
      const cur = p[mod] || [];
      return { ...p, [mod]: cur.includes(level) ? cur.filter(l => l !== level) : [...cur, level] };
    });
  }

  function applyBase(key) {
    setBasedOn(key);
    if (key && basePermissions[key]) setPermissions(JSON.parse(JSON.stringify(basePermissions[key])));
  }

  async function save() {
    if (!name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      const payload = { name, description, permissions, color, basedOn };
      const { data } = role?.id
        ? await api.put(`/roles/${role.id}`, payload)
        : await api.post('/roles', payload);
      onSaved(data, !role?.id);
      toast.success(role?.id ? 'Role updated' : 'Role created');
      onClose();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-800">{role?.id ? 'Edit Role' : 'Add New Role'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18}/></button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5 flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Role Name *</label>
              <input value={name} onChange={e => setName(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g. Supervisor"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Copy Permissions From</label>
              <select value={basedOn} onChange={e => applyBase(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Start from scratch</option>
                {Object.keys(basePermissions).map(k => (
                  <option key={k} value={k}>{roleDisplay(k)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Brief description of this role's responsibilities"/>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Role Colour</label>
            <div className="flex gap-2.5 flex-wrap">
              {COLORS.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  style={{ background: c, outline: color === c ? `3px solid ${c}` : 'none', outlineOffset: 2 }}
                  className="w-8 h-8 rounded-full transition-transform hover:scale-110"/>
              ))}
            </div>
          </div>

          {/* Permissions */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button onClick={() => setShowPerms(s => !s)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 transition-colors">
              <span className="flex items-center gap-2">
                <Shield size={14} className="text-primary-500"/>
                Permissions
                <span className="text-xs font-normal text-slate-400 ml-1">
                  ({Object.values(permissions).flat().length} granted across {Object.keys(MODULE_LABELS).length} modules)
                </span>
              </span>
              {showPerms ? <ChevronUp size={15}/> : <ChevronDown size={15}/>}
            </button>
            {showPerms && (
              <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-t border-slate-200 bg-white">
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-500">Module</th>
                    {PERM_LEVELS.map(l => <th key={l} className="px-4 py-2.5 font-semibold text-slate-500 capitalize text-center">{l}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(MODULE_LABELS).map(([mod, label]) => (
                    <tr key={mod} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-4 py-2.5 font-medium text-slate-700">{label}</td>
                      {PERM_LEVELS.map(level => (
                        <td key={level} className="px-4 py-2.5 text-center">
                          <input type="checkbox"
                            checked={!!(permissions[mod]?.includes(level))}
                            onChange={() => togglePerm(mod, level)}
                            className="w-4 h-4 rounded border-slate-300 text-primary-600 cursor-pointer focus:ring-primary-500"/>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <PayPermissionList items={payPermissions} permissions={permissions} onToggle={togglePerm}/>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm border border-slate-200 hover:bg-slate-50 text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2.5 rounded-xl text-sm bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 font-medium">
            {saving ? 'Saving…' : role?.id ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function RolesPermissions() {
  const [rolesData, setRolesData]         = useState({ baseRoles: [], customRoles: [] });
  const [basePermissions, setBasePermissions] = useState({});
  const [payPermissions, setPayPermissions]   = useState([]);
  const [selectedRole, setSelectedRole]   = useState(null);
  const [showAllBase, setShowAllBase]     = useState(false);
  const [tab, setTab]                     = useState('users');

  const [users, setUsers]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [limit, setLimit]       = useState(10);
  const [search, setSearch]     = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [roleModal, setRoleModal]       = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [moveTo, setMoveTo] = useState('');

  const loadRoles = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([api.get('/roles'), api.get('/roles/permissions')]);
      setRolesData(r.data);
      setBasePermissions(p.data.basePermissions || {});
      setPayPermissions(Array.isArray(p.data.payPermissions) ? p.data.payPermissions : []);
      if (!selectedRole && r.data.baseRoles?.length) setSelectedRole({ ...r.data.baseRoles[0], isCustom: false });
    } catch { toast.error('Failed to load roles'); }
  }, []);

  const loadUsers = useCallback(async () => {
    if (!selectedRole) return;
    setLoadingUsers(true);
    try {
      const roleKey = selectedRole.key === 'all' || selectedRole.key === 'custom_all' ? 'all' : selectedRole.key;
      const { data } = await api.get(`/roles/users?role=${encodeURIComponent(roleKey)}&search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load users'); }
    finally { setLoadingUsers(false); }
  }, [selectedRole, search, page, limit]);

  useEffect(() => { loadRoles(); }, [loadRoles]);
  useEffect(() => { if (tab === 'users') loadUsers(); }, [loadUsers, tab]);

  function selectRole(role, isCustom = false) {
    setSelectedRole({ ...role, isCustom });
    setPage(1);
    setSearch('');
  }

  // People on a role are never left on a deleted one: the admin picks where they go (another role,
  // or back to their base role). The server refuses with ROLE_IN_USE when the count here was stale.
  async function handleDeleteRole(role) {
    const inUse = (role.count || 0) > 0 || (role.users || []).length > 0;
    try {
      const { data } = await api.delete(`/roles/${role.id}`, inUse ? { data: { reassign_to: moveTo || null } } : undefined);
      toast.success(data?.moved ? `Role deleted. ${data.moved} moved ${moveTo ? 'to the new role' : 'back to their base role'}.` : 'Role deleted');
      setMoveTo('');
      setRolesData(p => ({ ...p, customRoles: p.customRoles.filter(r => r.id !== role.id) }));
      if (selectedRole?.id === role.id) selectRole(rolesData.baseRoles[0]);
      setDeleteConfirm(null);
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.code === 'ROLE_IN_USE') {
        const users = e.response.data.users || [];
        setDeleteConfirm({ ...role, users, count: users.length });
        return;
      }
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  }

  function onRoleSaved(data, isNew) {
    setRolesData(prev => {
      if (isNew) {
        const withoutAll = prev.customRoles.filter(r => r.key === 'custom_all');
        return { ...prev, customRoles: [...withoutAll, ...prev.customRoles.filter(r => r.key !== 'custom_all'), { ...data, count: 0 }] };
      }
      return { ...prev, customRoles: prev.customRoles.map(r => r.id === data.id ? { ...r, ...data } : r) };
    });
  }

  const SHOW_LIMIT = 7;
  const visibleBase   = showAllBase ? rolesData.baseRoles : rolesData.baseRoles.slice(0, SHOW_LIMIT);
  const hiddenCount   = Math.max(0, rolesData.baseRoles.length - SHOW_LIMIT);
  const totalPages    = Math.ceil(total / limit);

  const selectedPerms = selectedRole?.isCustom
    ? selectedRole.permissions || {}
    : selectedRole?.key && selectedRole.key !== 'all' && selectedRole.key !== 'custom_all'
      ? basePermissions[selectedRole.key] || {}
      : null;

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="Roles & permissions"
        description="User roles and what each one can access."
        actions={<button onClick={() => setRoleModal({})} className="btn-primary"><Plus size={15} /> Add New Role</button>}
      />

      {/* ── Base Roles ── */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Base Roles</h2>
          {hiddenCount > 0 && (
            <button onClick={() => setShowAllBase(s => !s)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              {showAllBase ? 'Show Less' : `Show More (${hiddenCount} more)`}
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-4">
          {visibleBase.map(role => (
            <RoleCard key={role.key} role={role}
              selected={selectedRole?.key === role.key && !selectedRole?.isCustom}
              onClick={() => selectRole(role, false)}/>
          ))}
        </div>
      </div>

      {/* ── Custom Roles ── */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Custom Roles</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-4">
          {rolesData.customRoles.map(role => (
            <RoleCard key={role.id || role.key} role={role} isCustom
              selected={selectedRole?.id === role.id || selectedRole?.key === (role.id || role.key)}
              onClick={() => selectRole({ key: role.id || role.key, ...role }, true)}
              onEdit={r => setRoleModal(r)}
              onDelete={r => { setMoveTo(''); setDeleteConfirm({ ...r, count: Number(r.count) || 0 }); }}/>
          ))}
          <button onClick={() => setRoleModal({})}
            className="rounded-2xl border-2 border-dashed border-slate-200 p-5 flex flex-col items-center justify-center text-slate-400 hover:border-primary-400 hover:text-primary-500 hover:bg-primary-50/30 transition-all cursor-pointer group">
            <div className="w-10 h-10 rounded-xl border-2 border-dashed border-current flex items-center justify-center mb-2 group-hover:border-primary-400">
              <Plus size={18}/>
            </div>
            <span className="text-xs font-medium">New Role</span>
          </button>
        </div>
      </div>

      {/* ── Tabs panel ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

        {/* Tab bar */}
        <div className="flex border-b border-slate-100 bg-slate-50/50">
          {['users','roles'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-8 py-4 text-sm font-semibold capitalize transition-all relative
                ${tab === t ? 'text-primary-600' : 'text-slate-500 hover:text-slate-700'}`}>
              {t === 'users' ? 'Users' : 'Roles'}
              {tab === t && <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-500 rounded-t"/>}
            </button>
          ))}
        </div>

        {/* ── Users tab ── */}
        {tab === 'users' && (
          <>
            {/* Filter + Search bar */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span className="font-medium">Filtering by:</span>
                <span className="flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-lg text-slate-700 font-medium text-xs">
                  {selectedRole?.name || 'All'} Base Roles
                  {selectedRole?.key !== 'all' && selectedRole?.key !== undefined && (
                    <button onClick={() => { selectRole({ key: 'all', name: 'All' }, false); }}
                      className="text-slate-400 hover:text-red-500 ml-0.5"><X size={11}/></button>
                  )}
                </span>
              </div>
              <div className="relative w-72">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search by Name or Email"
                  className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"/>
              </div>
            </div>

            {/* Table */}
            {loadingUsers ? (
              <div className="py-16 text-center"><div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"/></div>
            ) : users.length === 0 ? (
              <div className="py-16 text-center text-slate-400 text-sm">No users found</div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                    <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                      <td className="px-6 py-4">
                        <Link to={`/staff/${u.id}`} className="font-medium text-primary-600 hover:text-primary-700 hover:underline text-sm">
                          {u.first_name} {u.last_name}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${ROLE_BADGE[u.role] || 'bg-slate-100 text-slate-600'}`}>
                          {roleDisplay(u.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">{u.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/30">
                <div className="flex items-center gap-3 text-sm text-slate-500">
                  Showing {(page-1)*limit+1}–{Math.min(page*limit, total)} of {total} items
                  <div className="flex items-center gap-2 ml-2">
                    <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                      className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-primary-400">
                      {[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="text-slate-400">records per page</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-200 disabled:opacity-30 text-slate-600 text-sm">‹</button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pg = totalPages <= 5 ? i+1 : page <= 3 ? i+1 : page >= totalPages-2 ? totalPages-4+i : page-2+i;
                    return (
                      <button key={pg} onClick={() => setPage(pg)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors
                          ${pg===page ? 'bg-primary-600 text-white shadow-sm' : 'hover:bg-slate-200 text-slate-600'}`}>
                        {pg}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-200 disabled:opacity-30 text-slate-600 text-sm">›</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Roles/Permissions tab ── */}
        {tab === 'roles' && (
          <div className="p-6">
            {!selectedPerms ? (
              <div className="py-16 text-center text-slate-400">
                <Shield size={36} className="mx-auto mb-3 opacity-20"/>
                <p className="text-sm font-medium">Select a role card above to view its permissions</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                    style={{ background: `linear-gradient(135deg, ${(ROLE_GRADIENT[selectedRole?.key]||ROLE_GRADIENT.all).from}, ${(ROLE_GRADIENT[selectedRole?.key]||ROLE_GRADIENT.all).to})` }}>
                    <Shield size={16} className="text-white"/>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{selectedRole?.name} — Permissions</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{selectedRole?.description || 'Access control matrix for this role'}</p>
                  </div>

                  {/* Legend */}
                  <div className="ml-auto flex items-center gap-3">
                    {PERM_LEVELS.map(l => (
                      <div key={l} className="flex items-center gap-1.5 text-xs text-slate-500">
                        <span className={`w-2.5 h-2.5 rounded-full ${PERM_COLORS[l]}`}/>
                        <span className="capitalize">{l}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-48">Module</th>
                        {PERM_LEVELS.map(l => (
                          <th key={l} className="px-6 py-3.5 text-xs font-semibold text-slate-500 capitalize text-center">{l}</th>
                        ))}
                        <th className="px-6 py-3.5 text-xs font-semibold text-slate-500 text-right">Granted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(MODULE_LABELS).map(([mod, label]) => {
                        const modPerms = selectedPerms[mod] || [];
                        return (
                          <tr key={mod} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-3.5 font-semibold text-slate-700">{label}</td>
                            {PERM_LEVELS.map(level => (
                              <td key={level} className="px-6 py-3.5 text-center">
                                {modPerms.includes(level)
                                  ? <div className="flex justify-center"><div className={`w-5 h-5 rounded-full ${PERM_COLORS[level]} flex items-center justify-center shadow-sm`}><Check size={11} className="text-white stroke-[3]"/></div></div>
                                  : <div className="flex justify-center"><div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center"><Minus size={10} className="text-slate-300"/></div></div>
                                }
                              </td>
                            ))}
                            <td className="px-6 py-3.5 text-right">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${modPerms.some(l => PERM_LEVELS.includes(l)) ? 'bg-green-50 text-green-600' : 'bg-slate-50 text-slate-400'}`}>
                                {modPerms.filter(l => PERM_LEVELS.includes(l)).length}/{PERM_LEVELS.length}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  <PayPermissionList items={payPermissions} permissions={selectedPerms}/>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {roleModal !== null && (
        <RoleModal role={roleModal?.id ? roleModal : null} basePermissions={basePermissions} payPermissions={payPermissions}
          onClose={() => setRoleModal(null)} onSaved={onRoleSaved}/>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 mb-2">Delete "{deleteConfirm.name}"?</h3>
            {(deleteConfirm.count || 0) > 0 ? (
              <>
                <p className="text-sm text-slate-500 mb-3">
                  {deleteConfirm.count} {deleteConfirm.count === 1 ? 'person is' : 'people are'} on this role. Choose where they go before it is deleted.
                </p>
                {(deleteConfirm.users || []).length > 0 && (
                  <p className="text-xs text-slate-400 mb-3">{deleteConfirm.users.slice(0, 6).map(u => u.name).join(', ')}{deleteConfirm.users.length > 6 ? ` and ${deleteConfirm.users.length - 6} more` : ''}</p>
                )}
                <label className="block text-xs font-semibold text-slate-600 mb-1.5" htmlFor="role-move-to">Move them to</label>
                <select id="role-move-to" value={moveTo} onChange={e => setMoveTo(e.target.value)}
                  className="w-full mb-6 px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  <option value="">Their base role (no custom permissions)</option>
                  {rolesData.customRoles.filter(r => r.id && r.id !== deleteConfirm.id && r.key !== 'custom_all').map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </>
            ) : (
              <p className="text-sm text-slate-500 mb-6">No one is on this role. It will be removed.</p>
            )}
            <div className="flex justify-end gap-2.5">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2.5 rounded-xl text-sm border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDeleteRole(deleteConfirm)} className="px-4 py-2.5 rounded-xl text-sm bg-red-600 text-white hover:bg-red-700 font-medium">{(deleteConfirm.count || 0) > 0 ? 'Move and delete' : 'Delete Role'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
